import { useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useAuth } from '../../lib/AuthContext';
import { GoogleSignInButton } from '../../components/auth';
import { trpc } from '../../lib/trpc';

// Check if Google OAuth is configured
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

interface FormErrors {
  name?: string;
  email?: string;
  password?: string;
  confirmPassword?: string;
}

interface TouchedFields {
  name: boolean;
  email: boolean;
  password: boolean;
  confirmPassword: boolean;
}

/**
 * Registration page component
 * Handles new user registration with validation and auto-login
 */
export function RegisterPage() {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');
  const [touched, setTouched] = useState<TouchedFields>({
    name: false,
    email: false,
    password: false,
    confirmPassword: false,
  });

  // Check if Google OAuth is available on the backend
  const { data: googleOAuthStatus } = trpc.auth.isGoogleOAuthAvailable.useQuery(undefined, {
    // Only check if we have a client ID configured
    enabled: !!GOOGLE_CLIENT_ID,
  });
  const isGoogleOAuthAvailable = !!GOOGLE_CLIENT_ID && googleOAuthStatus?.available;

  /**
   * Handle successful Google sign-in
   */
  const handleGoogleSuccess = () => {
    navigate('/monitors', { replace: true });
  };

  /**
   * Handle Google sign-in error
   */
  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  /**
   * Validate form fields
   */
  const validateForm = useCallback((): FormErrors => {
    const errors: FormErrors = {};

    // Name validation (optional but nice to have)
    if (!name.trim()) {
      errors.name = 'Name is required';
    } else if (name.trim().length < 2) {
      errors.name = 'Name must be at least 2 characters';
    }

    // Email validation
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!password) {
      errors.password = 'Password is required';
    } else if (password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (password.length > 100) {
      errors.password = 'Password must be at most 100 characters';
    } else if (!/[A-Za-z]/.test(password) || !/[0-9]/.test(password)) {
      // Suggest stronger password but don't enforce
      // errors.password = 'Password should contain letters and numbers';
    }

    // Confirm password validation
    if (!confirmPassword) {
      errors.confirmPassword = 'Please confirm your password';
    } else if (password !== confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    return errors;
  }, [name, email, password, confirmPassword]);

  /**
   * Handle field blur for validation
   */
  const handleBlur = (field: keyof TouchedFields) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateForm();
    setFormErrors(errors);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Mark all fields as touched
    setTouched({
      name: true,
      email: true,
      password: true,
      confirmPassword: true,
    });

    // Validate form
    const errors = validateForm();
    setFormErrors(errors);

    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Submit registration request using useAuth hook
    setIsLoading(true);
    try {
      await register(email.trim().toLowerCase(), password);
      // Show success message with verification email info
      setRegisteredEmail(email.trim().toLowerCase());
      setRegistrationSuccess(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create account. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  /**
   * Toggle confirm password visibility
   */
  const handleToggleConfirmPassword = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  // Show success message after registration
  if (registrationSuccess) {
    return (
      <Box sx={{ textAlign: 'center' }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            mb: 3,
          }}
        >
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 64,
              color: 'success.main',
            }}
          />
        </Box>

        <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 2 }}>
          Registration successful!
        </Typography>

        <Alert severity="info" sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="body2" sx={{ mb: 1 }}>
            We've sent a verification email to <strong>{registeredEmail}</strong>.
          </Typography>
          <Typography variant="body2">
            Please check your inbox and click the verification link to enable email notifications.
          </Typography>
        </Alert>

        <Button
          variant="contained"
          size="large"
          fullWidth
          component={RouterLink}
          to="/login"
          sx={{ mb: 2, height: 48 }}
        >
          Continue to Login
        </Button>

        <Typography variant="body2" color="text.secondary">
          Didn't receive the email? Check your spam folder or contact support.
        </Typography>
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Create an account
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Start monitoring your websites today
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="name"
        name="name"
        label="Full Name"
        type="text"
        value={name}
        onChange={(e) => setName(e.target.value)}
        onBlur={() => handleBlur('name')}
        error={touched.name && !!formErrors.name}
        helperText={touched.name && formErrors.name}
        required
        autoComplete="name"
        autoFocus
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Full name',
          'aria-describedby': formErrors.name ? 'name-error' : undefined,
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="email"
        name="email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => handleBlur('email')}
        error={touched.email && !!formErrors.email}
        helperText={touched.email && formErrors.email}
        required
        autoComplete="email"
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Email address',
          'aria-describedby': formErrors.email ? 'email-error' : undefined,
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="password"
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => handleBlur('password')}
        error={touched.password && !!formErrors.password}
        helperText={
          touched.password && formErrors.password
            ? formErrors.password
            : 'Must be at least 8 characters'
        }
        required
        autoComplete="new-password"
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Password',
          'aria-describedby': formErrors.password ? 'password-error' : 'password-hint',
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={handleTogglePassword}
                edge="end"
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="confirmPassword"
        name="confirmPassword"
        label="Confirm Password"
        type={showConfirmPassword ? 'text' : 'password'}
        value={confirmPassword}
        onChange={(e) => setConfirmPassword(e.target.value)}
        onBlur={() => handleBlur('confirmPassword')}
        error={touched.confirmPassword && !!formErrors.confirmPassword}
        helperText={touched.confirmPassword && formErrors.confirmPassword}
        required
        autoComplete="new-password"
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Confirm password',
          'aria-describedby': formErrors.confirmPassword ? 'confirm-password-error' : undefined,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                onClick={handleToggleConfirmPassword}
                edge="end"
                disabled={isLoading}
              >
                {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 3 }}
      />

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        aria-label={isLoading ? 'Creating account' : 'Create account'}
        sx={{ mb: 2, height: 48 }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <span>Creating account...</span>
          </Box>
        ) : (
          'Create account'
        )}
      </Button>

      {isGoogleOAuthAvailable && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isLoading}
          />
        </>
      )}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Already have an account?{' '}
          <Link
            component={RouterLink}
            to="/login"
            sx={{ textDecoration: 'none' }}
            tabIndex={isLoading ? -1 : 0}
          >
            Sign in
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default RegisterPage;
